import {
	Component,
	ElementRef,
	OnDestroy,
	TemplateRef
}                                 from '@angular/core';
import { AbstractControl }        from '@angular/forms';
import { IMedia }                 from '@app/core/interfaces/media-interfaces';
import { AppInjector }            from '@app/core/services/app-injector.service';
import {
	SvgSizeEnum,
	SvgStateEnum,
	SvgTypeEnum
}                                 from '@app/shared/components/svg/svg.model';
import { LoggerService }          from '@app/shared/services/logger.service';
import {
	actionStatus,
	ToasterService
}                                 from '@app/shared/services/toaster.service';
import { UiService }              from '@app/shared/services/ui.service';
import { LIMIT_TYPES }            from '@appConfig/app-mode-config';
import { DeviceTypeEnum }         from '@appConfig/media-config';
import {
	RequestCompleteEntityState,
	RequestCompleteListState
}                                 from '@core/interfaces/api.interface';
import { ITabItem }               from '@core/interfaces/common.interfaces';
import { TranslationService }     from '@core/services/translation.service';
import {
	ArchivingScopesEnum,
	BusinessPremisesScopesEnum,
	ClientScopesEnum,
	ContractScopesEnum,
	EncryptionKeyScopesEnum,
	ErsScopesEnum,
	FccVersionScopesEnum,
	FeatureScopesEnum,
	FiskalScopesEnum,
	KeywordScopesEnum,
	MyAccountScopesEnum,
	NotificationScopesEnum,
	OrganizationScopesEnum,
	OutageEventScopesEnum,
	ScopeActionEnum,
	ScopePermissionCheckEnum,
	StatisticsScopesEnum,
	StructureExportScopesEnum,
	StructureImportScopesEnum,
	SupportAdminScopesEnum,
	TaskScopesEnum,
	TaxationScopesEnum,
	TenantScopesEnum,
	TssScopesEnum,
	UsageArchivingScopesEnum,
	UsageDtrScopesEnum,
	UsageElsterScopesEnum,
	UsageFiskalScopesEnum,
	UsageGkScopesEnum,
	UserScopesEnum
}                                 from '@models/scope.enums';
import {
	ActionDisplayModeEnum,
	ActionEnum,
	ActionIconSizeEnum,
	ActionIconTypeEnum
}                                 from '@shared/components/action-item/action-item.enum';
import { LinkItemTypeEnum }       from '@shared/components/link-item/link-item.model';
import {
	LoaderOverlaySvgAlignmentEnum,
	LoaderOverlaySvgPositionEnum
}                                 from '@shared/components/loader/loader.component';
import { StringUtils }            from '@utils/helpers';
import { DateUtilsHelperService } from '@utils/helpers/date-utils-helper.service';
import {
	Observable,
	Subject
}                                 from 'rxjs';
import { takeUntil }              from 'rxjs/operators';

@Component( {
	selector: 'app-base',
	template: '<div></div>'
} )
export class BaseComponent implements OnDestroy {

	private readonly _dateUtilsHelperService: DateUtilsHelperService;
	private readonly _translationService: TranslationService;

	protected clipboardContent: string;
	protected ngUnsubscribe: Subject<void> = new Subject();
	protected readonly toaster: ToasterService;

	public mediaData: IMedia;
	public readonly logger: LoggerService;
	public readonly uiService: UiService;
	public translationPath: string;

	// Enums
	public readonly actionIconTypeEnum = ActionIconTypeEnum;
	public readonly actionEnum = ActionEnum;
	public readonly actionDisplayModeEnum = ActionDisplayModeEnum;
	public readonly actionIconSizeEnum = ActionIconSizeEnum;
	public readonly deviceTypeEnum = DeviceTypeEnum;
	public readonly limitTypes = LIMIT_TYPES;
	public readonly linkItemTypeEnum = LinkItemTypeEnum;
	public readonly loaderOverlaySvgPositionEnum = LoaderOverlaySvgPositionEnum;
	public readonly loaderOverlaySvgAlignmentEnum = LoaderOverlaySvgAlignmentEnum;
	public readonly scopeActionEnum = ScopeActionEnum;
	public readonly scopePermissionCheckEnum = ScopePermissionCheckEnum;
	public readonly svgTypeEnum = SvgTypeEnum;
	public readonly svgSizeEnum = SvgSizeEnum;
	public readonly svgStateEnum = SvgStateEnum;

	// Todo move all enums here
	public readonly enums = {
		// actions: {
		//
		// },
		scopes: {
			archiving: ArchivingScopesEnum,
			businessPremises: BusinessPremisesScopesEnum,
			client: ClientScopesEnum,
			contract: ContractScopesEnum,
			encryptionKey: EncryptionKeyScopesEnum,
			ers: ErsScopesEnum,
			fccVersion: FccVersionScopesEnum,
			feature: FeatureScopesEnum,
			fiskal: FiskalScopesEnum,
			keyword: KeywordScopesEnum,
			myAccount: MyAccountScopesEnum,
			notification: NotificationScopesEnum,
			organization: OrganizationScopesEnum,
			outageEvent: OutageEventScopesEnum,
			statistics: StatisticsScopesEnum,
			structureExport: StructureExportScopesEnum,
			structureImport: StructureImportScopesEnum,
			supportAdmin: SupportAdminScopesEnum,
			task: TaskScopesEnum,
			taxationScopesEnum: TaxationScopesEnum,
			tenant: TenantScopesEnum,
			tss: TssScopesEnum,
			usageArchiving: UsageArchivingScopesEnum,
			usageDtr: UsageDtrScopesEnum,
			usageElster: UsageElsterScopesEnum,
			usageFiskal: UsageFiskalScopesEnum,
			usageGk: UsageGkScopesEnum,
			user: UserScopesEnum
		}
	};

	constructor() {
		const injector = AppInjector.getInjector();
		this._dateUtilsHelperService = injector.get( DateUtilsHelperService );
		this._translationService = injector.get( TranslationService );
		this.logger = injector.get( LoggerService );
		this.toaster = injector.get( ToasterService );
		this.uiService = injector.get( UiService );

		this.logger.trace( 'BaseComponent constructor' );

		this.uiService.getMediaData$()
		.pipe( takeUntil( this.ngUnsubscribe ) )
		.subscribe( ( media: IMedia ) => {
			this.mediaData = media;
		} );

	}

	// TODO: DFDEV-1551
	/**
	 * @param baseInternalTpl A template reference from the component's view,
	 *                        needed for the "off-screen textarea hack".
	 * @param showMessage A success message toast is shown if `true`.
	 */
	protected copyToClipboard( baseInternalTpl?: TemplateRef<ElementRef>, showMessage = false ): void {

		if ( !window.isSecureContext ) {
			/*
			 * preparations for "off-screen textarea hack" (needed to have
		   * copy-to-clipboard functionality in non-secure context)
		   * Note that the `Document.execCommand()` it employs is deprecated!
		 	 */
			let contentWrapper = document.body;
			if ( baseInternalTpl ) {
				contentWrapper = baseInternalTpl.elementRef.nativeElement.parentElement;
			}
			const copyToClipboardElement = document.createElement( 'textarea' );
			copyToClipboardElement.setAttribute( 'readonly', '' );
			copyToClipboardElement.style.position = 'absolute';
			copyToClipboardElement.style.left = '-99999px';
			copyToClipboardElement.value = this.clipboardContent;
			contentWrapper.prepend( copyToClipboardElement );
			copyToClipboardElement.focus();
			copyToClipboardElement.select();

			// copy
			try {
				document.execCommand( 'copy' );
				if ( showMessage ) {
					this.toaster.success(
						this.getI18nString( false, 'shared.copyToClipboard.success' )
					);
				}
			}
			catch ( error ) {}
			finally {
				// cleanup
				contentWrapper.removeChild( copyToClipboardElement );
			}
			return;
		}

		// regular copy-to-clipboard functionality in secure context (i.e. https)
		navigator.clipboard.writeText( this.clipboardContent )
		.then( () => {
			if ( showMessage ) {
				this.toaster.success(
					this.getI18nString( false, 'shared.copyToClipboard.success' )
				);
			}
		} )
		.catch( ( error: DOMException ) => this.logger.error( error ) );
	}

	ngOnDestroy() {
		this.logger.trace( 'BaseComponent ngOnDestroy' );
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	showErrors( f: AbstractControl ): boolean {
		this.logger.trace( 'BaseComponent showErrors' );
		const showErrors = f.invalid && (
			f.dirty || f.touched
		);

		this.logger.debug(
			`input# AbstractControl: ${ f }`,
			`output# showErrors: ${ showErrors }`
		);

		return showErrors;
	}

	getI18nStringByPathWithValue( useTranslationPath: boolean, path: string, value: any, checkIfIsTranslated: boolean = true, spareString?: string ): string {
		if ( !value ) { return spareString ?? ''; }
		return this._translationService.getI18nString( useTranslationPath, this.translationPath, `${ path }.${ value }`, checkIfIsTranslated, spareString );
	}

	getI18nString( useTranslationPath: boolean, path: string, checkIfIsTranslated: boolean = true, spareString?: string ): string {
		return this._translationService.getI18nString( useTranslationPath, this.translationPath, path, checkIfIsTranslated, spareString );
	}

	getI18nStringParams( useTranslationPath: boolean, path: string, params?: {}, checkIfIsTranslated: boolean = true, spareString?: string ): string {
		return this._translationService.getI18nStringParams( useTranslationPath, this.translationPath, path, params, checkIfIsTranslated, spareString );
	}

	getI18nObservable( useTranslationPath: boolean, path: string ): Observable<any> {
		return this._translationService.getI18nObservable( useTranslationPath, this.translationPath, path );
	}

	getLocaleDateString( date: Date, options?: Intl.DateTimeFormatOptions, spare: string = '' ): string {
		return this._dateUtilsHelperService.getLocaleDateString( date, options, spare );
	}

	/**
	 * Will replace an empty value by a spare string.
	 * "Empty value" means nullish (`null` or `undefined`) or empty string (`''`).
	 * @param value The value to be replaced if empty.
	 * @param spare The spare string replacing the empty value.
	 * 							Default spare is simple dash (`'-'`).
	 */
	replaceEmptyValue( value, spare: string = '-' ) {
		return StringUtils.replaceEmptyValue( value, spare );
	}

	getTabItemDataByIdentifier( tabs: ITabItem[], identifier: string ): ITabItem {
		let foundTabItem: ITabItem = null;

		tabs.some( ( tabItem: ITabItem ) => {
			if ( tabItem.tabIdentifier === identifier ) {
				foundTabItem = tabItem;
			}
		} );

		return foundTabItem;
	}

	getApiRequestStateMessage(
		state: RequestCompleteEntityState<any> | RequestCompleteListState<any>,
		entityName: string,
		action: actionStatus,
		onlyError?: boolean,
		showDefaultBeforeApiErrorMessage?: boolean
	): string {
		return this._translationService.getApiRequestStateMessage( state, entityName, action, onlyError, showDefaultBeforeApiErrorMessage );
	}
}
